import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Doughnut } from "react-chartjs-2";
import { ChartData, ChartOptions, Plugin } from "chart.js";

import { RootState } from "../../../../../store/store";
import theme from "../../../../../components/styles/theme/theme";

const COLORS = [
  "#C10000",
  "#8B1A1A",
  "#D4604E",
  "#F4A49E",
  "#F8C8C2",
  "#B8860B",
  "#C9A84C",
  "#D4C97A",
  "#A0A0A0",
  "#B8B8B8",
  "#D5D5D5",
  "#E8D8A0",
  "#6E6E6E",
  "#E0C080",
  "#C0A060",
  "#F0E0C0",
  "#A08060",
  "#808080",
  "#D0B090",
  "#E8E8E8",
];

const formatDollarValue = (value: number): string => {
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}B`;
  if (value >= 10) return `$${value.toFixed(1)}M`;
  if (value >= 1) return `$${value.toFixed(2)}M`;
  if (value >= 0.01) return `$${(value * 1000).toFixed(0)}K`;
  return `$${value}M`;
};

// Minimum percentage to show an outer label
const MIN_LABEL_PCT = 2.5;

interface LabelEntry {
  anchorX: number;
  anchorY: number;
  labelX: number;
  labelY: number;
  text: string;
  side: "left" | "right";
  color: string;
}

const outerLabelsPlugin: Plugin<"doughnut"> = {
  id: "outerLabels",
  afterDraw(chart) {
    const { ctx } = chart;
    const meta = chart.getDatasetMeta(0);
    if (!meta?.data?.length) return;

    const dataset = chart.data.datasets[0];
    const bgColors = dataset.backgroundColor as string[];
    const values = dataset.data as number[];
    const total = values.reduce((s, v) => s + (v as number), 0);
    if (total === 0) return;

    const firstArc = meta.data[0] as any;
    const cx = firstArc.x;
    const cy = firstArc.y;
    const outerR = firstArc.outerRadius;

    ctx.save();
    ctx.font = "bold 11px Inter, sans-serif";

    const entries: LabelEntry[] = [];

    meta.data.forEach((arc, i) => {
      const value = values[i] as number;
      if (!value || value === 0) return;

      const pct = (value / total) * 100;
      if (pct < MIN_LABEL_PCT) return;

      const { startAngle, endAngle } = arc as any;
      const midAngle = (startAngle + endAngle) / 2;

      // Anchor: just outside arc edge
      const anchorR = outerR + 5;
      const anchorX = cx + Math.cos(midAngle) * anchorR;
      const anchorY = cy + Math.sin(midAngle) * anchorR;

      // Label position: further out along same angle
      const labelR = outerR + 30;
      const labelX = cx + Math.cos(midAngle) * labelR;
      const labelY = cy + Math.sin(midAngle) * labelR;

      const side: "left" | "right" =
        midAngle > Math.PI / 2 && midAngle < (3 * Math.PI) / 2
          ? "left"
          : "right";

      entries.push({
        anchorX,
        anchorY,
        labelX,
        labelY,
        text: formatDollarValue(value),
        side,
        color: bgColors[i] || "#C10000",
      });
    });

    // Resolve vertical overlaps per side
    const leftEntries = entries.filter((e) => e.side === "left");
    const rightEntries = entries.filter((e) => e.side === "right");

    const resolveOverlaps = (labels: LabelEntry[]) => {
      labels.sort((a, b) => a.labelY - b.labelY);
      const minGap = 18;
      for (let pass = 0; pass < 10; pass++) {
        let anyOverlap = false;
        for (let i = 1; i < labels.length; i++) {
          const gap = labels[i].labelY - labels[i - 1].labelY;
          if (gap < minGap) {
            const push = (minGap - gap) / 2 + 0.5;
            labels[i - 1].labelY -= push;
            labels[i].labelY += push;
            anyOverlap = true;
          }
        }
        if (!anyOverlap) break;
      }
    };

    resolveOverlaps(leftEntries);
    resolveOverlaps(rightEntries);

    // Draw labels
    [...leftEntries, ...rightEntries].forEach(
      ({ anchorX, anchorY, labelX, labelY, text, side, color }) => {
        // Horizontal tail from label position
        const tailLen = 15;
        const tailX = side === "left" ? labelX - tailLen : labelX + tailLen;

        // Draw line: anchor → label position → horizontal tail
        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.lineWidth = 1;
        ctx.moveTo(anchorX, anchorY);
        ctx.lineTo(labelX, labelY);
        ctx.lineTo(tailX, labelY);
        ctx.stroke();

        // Draw text at end of tail
        ctx.fillStyle = theme.palette.primary["800"];
        ctx.textBaseline = "middle";
        if (side === "left") {
          ctx.textAlign = "right";
          ctx.fillText(text, tailX - 3, labelY);
        } else {
          ctx.textAlign = "left";
          ctx.fillText(text, tailX + 3, labelY);
        }
      }
    );

    ctx.restore();
  },
};

const CommunityContributionsChart: React.FC = () => {
  const {
    statistics: { statisticsForSelectedCountry },
  } = useSelector((state: RootState) => state);

  const communityData =
    statisticsForSelectedCountry?.data_points.community_impact;
  const categories = communityData?.categories ?? [];

  const total = useMemo(
    () => categories.reduce((sum, c) => sum + c.value, 0),
    [categories]
  );

  const labels = categories.map((c) => c.label);
  const values = categories.map((c) => c.value);
  const colors = categories.map((_, i) => COLORS[i % COLORS.length]);

  const data: ChartData<"doughnut"> = {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: colors,
        borderColor: theme.palette.common.white,
        borderWidth: 2,
      },
    ],
  };

  const options: ChartOptions<"doughnut"> = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { top: 40, bottom: 40, left: 90, right: 90 },
    },
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        display: (ctx) => {
          const value = ctx.dataset.data[ctx.dataIndex] as number;
          const pct = total > 0 ? (value / total) * 100 : 0;
          return pct >= 8;
        },
        formatter: (value: number) => {
          const pct = total > 0 ? ((value / total) * 100).toFixed(1) : "0";
          return `${pct}%`;
        },
        color: "#FFFFFF",
        font: {
          weight: "bold",
          size: 14,
        },
        anchor: "center",
        align: "center",
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.parsed;
            const percentage =
              total > 0 ? ((value / total) * 100).toFixed(1) : "0";
            return `${context.label}: ${formatDollarValue(value)} (${percentage}%)`;
          },
        },
      },
    },
  };

  if (categories.length === 0) {
    return null;
  }

  return (
    <>
      <div style={{ height: "580px", width: "100%" }}>
        <Doughnut
          data={data}
          options={options}
          plugins={[outerLabelsPlugin]}
        />
      </div>
      <CommunityLegend labels={labels} colors={colors} />
    </>
  );
};

const CommunityLegend: React.FC<{
  labels: string[];
  colors: string[];
}> = ({ labels, colors }) => {
  const columns = Math.min(5, labels.length);
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, auto)`,
        gap: "8px 24px",
        marginTop: "32px",
        justifyContent: "center",
      }}
    >
      {labels.map((label, index) => (
        <div
          key={index}
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: "12px",
              minWidth: "12px",
              height: "12px",
              backgroundColor: colors[index],
              marginRight: "8px",
            }}
          />
          <span
            style={{
              fontSize: "12px",
              color: theme.palette.primary["600"],
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </span>
        </div>
      ))}
    </div>
  );
};

export default CommunityContributionsChart;
